import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Search, FlaskConical, LayoutGrid, Send, Mic, Copy, Check, Volume2, Square, Loader2, X, ChevronDown, AlertCircle, Share2 } from "lucide-react";
import "./AdaptiveCardStyles.css";
import "./Chat.css";
import { AdaptiveCard } from "../AdaptiveCard/AdaptiveCard.tsx";
import { useNavigate } from "react-router-dom";
import { CHATCONST, ERRORMESSAGES, BASEURL, ERROR_MESSAGE_TIMEOUT, STORAGE_KEYS } from "../../utils/constants";
import { useSelector, useDispatch } from "react-redux";
import { setChatMessages, setShowChat } from "../../utils/chatSlice";
import { setUserDetails } from "../../utils/loginSlice";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import ModalComponent from "../Modal/ModalComponent.js";
import { setOpenModal, setParams } from "../../utils/appSlice";
import useOnline from "../../utils/useOnline";
import { useMsal } from "@azure/msal-react";
import { marked } from 'marked';
import { sanitizeHtml } from "../../utils/sanitize";
import BeagleCardLibrary from "beagle-card-library";
import StreamViewer from "../StreamViewer/StreamViewer.jsx";
import { useTextToSpeech } from "../../hooks/useTextToSpeech.js";
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { useIsMobile } from "../../hooks/useMediaQuery";
import { getUserDetails } from "../../utils/authUtils";

/**
 * ChatComponent Module
 * @module ChatComponent
 */
const ChatComponent = () => {

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [typingActive, setTypingActive] = useState(false);
  const userDetails = getUserDetails();
  const token = userDetails?.tts_info?.token;
  const region = userDetails?.tts_info?.region;
  const conversationId = userDetails?.conversationId;
  const [errorMessage, setErrorMessage] = useState("");
  const [socket, setSocket] = useState(null);
  const [imagePopup, setImagePopup] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [teamsUrl, setTeamsUrl] = useState("");
  const [queriesModal, setQueriesModal] = useState(false);
  const [activeVAIndex, setActiveVAIndex] = useState(-1);
  const [isSocketConnection, setIsSocketConnection] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const pendingMessagesRef = useRef([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const ssoReauthAttemptedRef = useRef(false);
  const { instance, accounts } = useMsal();
  const [vaPrompts, setVAPrompts] = useState([]);
  const [micState, setMicState] = useState(false);
  const recognizerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const imageRef = useRef(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [startResearch, setStartResearch] = useState(false);
  const [streamUrl, setStreamUrl] = useState("");
  const [streamStatus, setStreamStatus] = useState("");
  const [streamHeading, setStreamHeading] = useState("");
  const [, setUserSelectedStream] = useState(false);
  const userSelectedStreamRef = useRef("");
  const isDprQueryRunningRef = useRef(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // FLIP animation refs for centered-to-bottom transition
  const inputWrapperRef = useRef(null);
  const inputPositionRef = useRef(null);
  const shouldAnimateRef = useRef(false);

  // WebSocket management refs
  const socketRef = useRef(null);
  const globalRetryCountRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);

  // Mutex flags for preventing race conditions
  const isSetupInProgressRef = useRef(false);
  const isTokenRefreshInProgressRef = useRef(false);
  const isErrorHandlingInProgressRef = useRef(false);
  const isAuthFailureInProgressRef = useRef(false);

  const isMobile = useIsMobile();

  const clearUserSelectedStream = () => {
    userSelectedStreamRef.current = "";
    setUserSelectedStream(false);
  };

  const queueMessage = (messagePayload) => {
    pendingMessagesRef.current.push(messagePayload);
    setPendingMessages([...pendingMessagesRef.current]);
  };

  const flushMessageQueue = () => {
    if (pendingMessagesRef.current.length === 0) return;

    const messagesToSend = [...pendingMessagesRef.current];
    pendingMessagesRef.current = [];
    setPendingMessages([]);

    for (const msg of messagesToSend) {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send(JSON.stringify(msg));
        } catch (error) {
          console.error("Failed to send queued message:", error);
          queueMessage(msg);
        }
      } else {
        queueMessage(msg);
      }
    }
  };

  const cleanupWebSocket = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (socketRef.current) {
      try {
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;

        if (socketRef.current.readyState !== WebSocket.CLOSED &&
            socketRef.current.readyState !== WebSocket.CLOSING) {
          socketRef.current.close(1000, "Component cleanup");
        }
      } catch (error) {
        console.error("Error during socket cleanup:", error);
      }
      socketRef.current = null;
    }

    setSocket(null);
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const textareaRef = useRef(null);
  const isOnline = useOnline();
  const error_message_timeOut = ERROR_MESSAGE_TIMEOUT;
  const scroll_timer = 1000;
  const typing_timer = 5000;

  const params = Object.entries(useSelector((state) => state.app.params));
  const showChat = useSelector((store) => store.chat.showChat);
  const { textToSpeech, ttsState, stopSpeech } = useTextToSpeech(token, region);

  // Track which chat message is currently being narrated
  const [activeTtsMessageId, setActiveTtsMessageId] = useState(null);

  useEffect(() => {
    if (ttsState === 'idle') setActiveTtsMessageId(null);
  }, [ttsState]);

  // Track active left button
  const [activeLeftBtn, setActiveLeftBtn] = useState("search");

  useEffect(() => {
    if (!params.length) return;

    const text = Array.from(params.entries())
      .map(([key, value], index) => `${value[0]}: ${value[1]} ${index < params.length - 1 ? ',' : ''}`)
      .join(' ');

    setInputMessage("show " + text);
    dispatch(setParams({}));
  }, []);

  const getVADetails = async () => {
    try {
      const response = await fetch(
        `${BASEURL}/api/conversations/virtual-analyst/details/vas`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            "platform": "teams",
            "port_no": "8085",
            "user_id": userDetails.aad_object_id,
            "user_name": userDetails.email,
            "tenant_id": userDetails.tenantId,
            "appId": { "appIdAsString": "" },
            "team_id": "",
            "destination_id": "",
            "channelId": "webapp",
            "teams_channel_id": "msteams",
            "entityId": "",
            "bot_id": userDetails.bot_id
          })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(ERRORMESSAGES.FAILEDTOFETCHVADETAILS);
        setTimeout(() => setErrorMessage(""), error_message_timeOut);
        return;
      }
      let va_details = data?.va_details;
      va_details = va_details.filter(va => va?.is_user_active);
      setVAPrompts(va_details || []);
    } catch (error) {
      setErrorMessage(ERRORMESSAGES.ERRORFETCHINGVADETAILS);
    }
  };

  useEffect(() => {
    getVADetails();
  }, []);

  const refreshAccessToken = async () => {
    if (isTokenRefreshInProgressRef.current) {
      for (let i = 0; i < 50; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!isTokenRefreshInProgressRef.current) {
          const currentUserDetails = getUserDetails();
          return currentUserDetails?.token || null;
        }
      }
      return null;
    }

    isTokenRefreshInProgressRef.current = true;

    try {
      const currentUserDetails = getUserDetails();
      if (!currentUserDetails?.refresh) {
        return null;
      }

      const response = await fetch(`${BASEURL}/api/auth/token/refresh/`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh: currentUserDetails.refresh })
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (isAuthFailureInProgressRef.current) return null;
      currentUserDetails.token = data.access;
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(currentUserDetails));
      return data.access;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    } finally {
      isTokenRefreshInProgressRef.current = false;
    }
  };

  const trySilentSsoReauth = async () => {
    try {
      const activeAccount = instance.getActiveAccount() || accounts[0];
      const scopes = ["openid", "profile", "email"];
      let response;

      if (activeAccount) {
        response = await instance.acquireTokenSilent({
          scopes,
          account: activeAccount,
          forceRefresh: true,
        });
      } else {
        /*
         * MSAL's cache is sessionStorage (per-tab), so a tab opened after the
         * original sign-in holds no MSAL account of its own -- even though the
         * app session in localStorage is shared and this tab is logged in.
         * Re-establish the account silently against the existing Entra session
         * using the email we already have, so a second tab never has to sign
         * in again. Falls through to the normal auth-failure path if the Entra
         * session is gone or its cookie is unavailable in an iframe.
         */
        const loginHint = getUserDetails()?.email;
        if (!loginHint) return false;

        response = await instance.ssoSilent({ scopes, loginHint });
        if (response?.account) {
          instance.setActiveAccount(response.account);
        }
      }

      const ssoToken = response?.idToken || response?.accessToken;
      if (!ssoToken) return false;

      const backendResponse = await fetch(`${BASEURL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ssoToken}`,
        },
        body: JSON.stringify({
          email: response.account?.username || activeAccount?.username,
        }),
      });

      if (!backendResponse.ok) return false;

      const result = await backendResponse.json();
      if (result?.error) return false;

      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(result));
      return true;
    } catch (error) {
      if (error.name === "InteractionRequiredAuthError") return false;
      console.error("Silent SSO re-auth failed:", error);
      return false;
    }
  };

  const setupWebSocketWithRefresh = async (url) => {
    if (isAuthFailureInProgressRef.current) return;

    const newToken = await refreshAccessToken();
    const tokenToUse = newToken || getUserDetails()?.token;

    if (tokenToUse) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('t', tokenToUse);
      setupWebSocket(urlObj.toString(), tokenToUse);
    } else {
      handleAuthFailure();
    }
  };

  const setupWebSocket = useCallback((url, token) => {
    if (isSetupInProgressRef.current) return;

    isSetupInProgressRef.current = true;

    try {
      cleanupWebSocket();

      const urlObj = new URL(url);
      const newSocket = new WebSocket(urlObj.toString());
      socketRef.current = newSocket;

      const HEARTBEAT_INTERVAL = 30000;

      const startHeartbeat = () => {
        stopHeartbeat();
        heartbeatIntervalRef.current = setInterval(() => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            try {
              socketRef.current.send(JSON.stringify({ type: "ping" }));
            } catch (error) {
              console.error("Heartbeat error:", error);
              stopHeartbeat();
            }
          }
        }, HEARTBEAT_INTERVAL);
      };

      const stopHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };

      newSocket.onopen = () => {
        setIsSocketConnection(true);
        setIsReconnecting(false);
        setConnectionStatus('connected');
        globalRetryCountRef.current = 0;
        startHeartbeat();
        flushMessageQueue();
        isSetupInProgressRef.current = false;
      };

      newSocket.onmessage = handleWebSocketMessage;

      newSocket.onclose = (event) => {
        setIsSocketConnection(false);
        stopHeartbeat();
        isSetupInProgressRef.current = false;

        if (event.code !== 1000) {
          setConnectionStatus('reconnecting');
          handleWebSocketError(new Error(`WebSocket closed: ${event.code}`));
        }
      };

      newSocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        stopHeartbeat();
        isSetupInProgressRef.current = false;
        handleWebSocketError(error);
      };

      setSocket(newSocket);
    } catch (error) {
      console.error("Error in setupWebSocket:", error);
      isSetupInProgressRef.current = false;
    }
  }, [cleanupWebSocket, flushMessageQueue]);

  const handleWebSocketMessage = (event) => {
    let newMessage;
    try {
      newMessage = JSON.parse(event.data);
    } catch (e) {
      console.error("Failed to parse WebSocket message:", e);
      return;
    }

    if (newMessage.type === "typing") {
      setTypingActive(true);
    } else {
      setTypingActive(false);
    }

    if (newMessage.type === "message") {
      try {
        if (newMessage.attachments && newMessage.attachments.length === 1) {
          const attachment_body = newMessage.attachments?.[0]?.content?.body;
          if (attachment_body && attachment_body.length > 1) {
            const research_plans = attachment_body[1]?.items;
            if (research_plans && research_plans.length > 0) {
              for (const [index, plan] of research_plans.entries()) {
                if (plan.url !== "" && plan.status === "running" && index === 0) {
                  if (!userSelectedStreamRef.current) {
                    setStartResearch(true);
                    setStreamHeading(plan.heading || "Research Plan");
                    setStreamStatus(plan.status || "running");
                    setStreamUrl(plan.url);
                  }
                  break;
                }
              }
              if (!isDprQueryRunningRef.current && research_plans.some(plan => plan.status === "running")) {
                isDprQueryRunningRef.current = true;
              } else if (isDprQueryRunningRef.current && !research_plans.some(plan => plan.status === "running")) {
                isDprQueryRunningRef.current = false;
              }
            }
          }
        }
      } catch (e) {
        // Research plan extraction failed — continue to render the message
      }
    }

    if (newMessage.type !== "message" && newMessage.type !== "beagle_cards") {
      return;
    }

    setMessages((prevMessages) => {
      const newId = newMessage.activity_id || newMessage.id;
      let messageIndex = prevMessages.findIndex((msg) => {
        const existingId = msg.activity_id || msg.id;
        return existingId && newId && existingId === newId;
      });

      if (messageIndex === -1 && newMessage.text) {
        messageIndex = prevMessages.findIndex(
          (msg) => msg.pending && msg.text?.trim() === newMessage.text?.trim()
        );
      }

      if (messageIndex === -1 && newMessage.text && newMessage.from?.id) {
        const recentMessages = prevMessages.slice(-5);
        const recentIndex = recentMessages.findIndex(
          (msg) => msg.text?.trim() === newMessage.text?.trim() && msg.from?.id === newMessage.from?.id
        );
        if (recentIndex !== -1) {
          messageIndex = prevMessages.length - 5 + recentIndex;
        }
      }

      if (messageIndex > -1) {
        const updatedMessages = [...prevMessages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          ...newMessage,
          pending: false,
        };
        return updatedMessages;
      } else {
        return [...prevMessages, newMessage];
      }
    });
  };

  const handleInputChange = (e) => {
    const textarea = textareaRef.current;
    setInputMessage(e.target.value);

    textarea.style.height = "auto";
    const computedStyle = getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight);
    const verticalPadding = parseInt(computedStyle.paddingTop) + parseInt(computedStyle.paddingBottom);
    const maxHeight = lineHeight * 3 + verticalPadding;
    const minHeight = lineHeight * textarea.rows + verticalPadding;
    textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
  };

  const handleWebSocketError = useCallback(async (error) => {
    setIsSocketConnection(false);

    if (isErrorHandlingInProgressRef.current) return;

    isErrorHandlingInProgressRef.current = true;

    const maxRetries = 5;
    const baseDelay = 1000;
    const maxDelay = 30000;

    try {
      if (isSocketConnection) {
        setIsReconnecting(false);
        setConnectionStatus('connected');
        return;
      }

      if (globalRetryCountRef.current < maxRetries) {
        setIsReconnecting(true);
        setConnectionStatus('reconnecting');

        const delay = Math.min(
          baseDelay * Math.pow(2, globalRetryCountRef.current),
          maxDelay
        );

        globalRetryCountRef.current++;
        cleanupWebSocket();

        setTimeout(async () => {
          try {
            if (!isSocketConnection && userDetails?.streamUrl) {
              await setupWebSocketWithRefresh(userDetails.streamUrl);
            }
          } catch (retryError) {
            console.error("Retry attempt failed:", retryError);
          } finally {
            isErrorHandlingInProgressRef.current = false;
          }
        }, delay);
      } else {
        if (!ssoReauthAttemptedRef.current) {
          ssoReauthAttemptedRef.current = true;
          setConnectionStatus('reconnecting');

          const ssoReauthSuccess = await trySilentSsoReauth();

          if (ssoReauthSuccess) {
            ssoReauthAttemptedRef.current = false;
            globalRetryCountRef.current = 0;

            const freshUserDetails = getUserDetails();
            await setupWebSocketWithRefresh(freshUserDetails?.streamUrl);
          } else {
            handleAuthFailure();
          }
        } else {
          handleAuthFailure();
        }

        isErrorHandlingInProgressRef.current = false;
      }
    } catch (error) {
      console.error("Error in handleWebSocketError:", error);
      isErrorHandlingInProgressRef.current = false;
    }
  }, [cleanupWebSocket, isSocketConnection, userDetails]);

  const handleAuthFailure = () => {
    if (isAuthFailureInProgressRef.current) return;
    isAuthFailureInProgressRef.current = true;

    ssoReauthAttemptedRef.current = false;
    setIsReconnecting(false);
    setConnectionStatus('disconnected');
    globalRetryCountRef.current = 0;
    cleanupWebSocket();

    dispatch(setShowChat(false));
    dispatch(setUserDetails({}));

    localStorage.clear();
    sessionStorage.clear();
    sessionStorage.setItem(STORAGE_KEYS.LOGOUT_FLAG, "true");

    setTimeout(() => navigate("/login"), 2000);
  };

  const fetchData = async (conversationId) => {
    const pastActivities = await fetchPastActivities(conversationId);
    setMessages(pastActivities);
    dispatch(setChatMessages(pastActivities));
    await setupWebSocketWithRefresh(userDetails?.streamUrl);
  };

  useEffect(() => {
    if (!userDetails.conversationId) {
      setErrorMessage(ERRORMESSAGES.REDIRECTTOLOGIN);
      navigate("/login");
      return;
    }

    fetchData(userDetails.conversationId);

    return () => {
      cleanupWebSocket();
      globalRetryCountRef.current = 0;
      isSetupInProgressRef.current = false;
      isTokenRefreshInProgressRef.current = false;
      isErrorHandlingInProgressRef.current = false;
      ssoReauthAttemptedRef.current = false;
      isAuthFailureInProgressRef.current = false;
    };
  }, [cleanupWebSocket]);

  useEffect(() => {
    if (!isOnline) {
      setConnectionStatus('offline');
    } else if (isSocketConnection) {
      setConnectionStatus('connected');
    } else if (isReconnecting) {
      setConnectionStatus('reconnecting');
    }
  }, [isOnline, isSocketConnection, isReconnecting]);

  const fetchPastActivities = async (conversationId) => {
    try {
      const response = await fetch(
        `${BASEURL}/api/conversations/${conversationId}/activities`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
          },
        }
      );
      const data = await response.json();
      if (data.code === "token_not_valid") {
        fetchUpdatedToken(conversationId);
      }
      if (!response.ok) {
        setErrorMessage(ERRORMESSAGES.FAILEDTOFETCHPASTACTIVITIES);
        setTimeout(() => setErrorMessage(""), error_message_timeOut);
        return [];
      }
      return data.activities || [];
    } catch (error) {
      setErrorMessage(ERRORMESSAGES.ERRORFETCHINGPASTACTIVITIES);
      navigate("/login");
      return [];
    }
  };

  const reFetchPastActivities = async (conversationId, token) => {
    setupWebSocketWithRefresh(userDetails?.streamUrl);
    try {
      const response = await fetch(
        `${BASEURL}/api/conversations/${conversationId}/activities`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(ERRORMESSAGES.FAILEDTOFETCHPASTACTIVITIES);
        navigate("/login");
        return [];
      }
      setMessages(data.activities);
      dispatch(setChatMessages(data.activities));
      return data.activities;
    } catch (error) {
      setErrorMessage(ERRORMESSAGES.ERRORFETCHINGPASTACTIVITIES);
      navigate("/login");
      return [];
    }
  };

  useEffect(() => {
    const chatBox = chatContainerRef.current;
    const imageElement = chatBox?.lastChild?.getElementsByTagName("img")?.[1];

    const handleImageLoad = () => {
      if (!isUserScrolledUp) scrollToBottom();
    };

    imageElement?.addEventListener("load", handleImageLoad);

    const resetTimer = !isUserScrolledUp
      ? setTimeout(() => scrollToBottom(), scroll_timer)
      : null;

    return () => {
      imageElement?.removeEventListener("load", handleImageLoad);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [messages, isUserScrolledUp]);

  useEffect(() => {
    if (!isUserScrolledUp) {
      const refreshTimer = setTimeout(() => scrollToBottom(), scroll_timer);
      return () => clearTimeout(refreshTimer);
    }
  }, [isUserScrolledUp]);

  // Captures input position before layout change, then dispatches showChat.
  // On mobile, showChat starts as true (chatSlice), so this is a no-op — animation skipped intentionally.
  const activateChat = () => {
    if (!showChat) {
      if (inputWrapperRef.current) {
        inputPositionRef.current = inputWrapperRef.current.getBoundingClientRect().top;
      }
      shouldAnimateRef.current = true;
      dispatch(setShowChat(true));
    }
  };

  // FLIP animation: after DOM update, animate input from old center to new bottom position
  useLayoutEffect(() => {
    let fallbackTimer;

    if (showChat && inputPositionRef.current !== null && inputWrapperRef.current) {
      const newTop = inputWrapperRef.current.getBoundingClientRect().top;
      const delta = inputPositionRef.current - newTop;

      if (Math.abs(delta) > 1) {
        const el = inputWrapperRef.current;
        el.style.transform = `translateY(${delta}px)`;
        el.style.transition = 'none';
        // FLIP: Force browser reflow so the 'none' transition is applied
        // before we set the new transition. DO NOT REMOVE this line.
        void el.offsetHeight;
        el.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.transform = 'translateY(0)';

        const onEnd = () => {
          clearTimeout(fallbackTimer);
          el.removeEventListener('transitionend', onEnd);
          el.style.transition = '';
          el.style.transform = '';
        };
        el.addEventListener('transitionend', onEnd, { once: true });
        fallbackTimer = setTimeout(onEnd, 600);
      }
      inputPositionRef.current = null;
    }

    // Reset animation ref after this render cycle
    shouldAnimateRef.current = false;

    return () => {
      clearTimeout(fallbackTimer);
      if (inputWrapperRef.current) {
        inputWrapperRef.current.style.transition = '';
        inputWrapperRef.current.style.transform = '';
      }
    };
  }, [showChat]);

  const sendMessage = (query) => {
    if (!showChat) {
      activateChat();
      setIsUserScrolledUp(false);
      scrollToBottom();
    }

    const messagePayload = {
      type: "message",
      tool: activeLeftBtn,
      text: inputMessage || query,
      from: { id: userDetails.email },
      tenant_id: userDetails.tenantId,
      localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    setMessages((prev) => [...prev, { ...messagePayload, id: Date.now(), pending: true }]);
    setInputMessage("");
    setIsUserScrolledUp(false);
    scrollToBottom();

    if (userDetails && socketRef.current?.readyState === WebSocket.OPEN) {
      setTypingActive(true);

      try {
        socketRef.current.send(JSON.stringify(messagePayload));
      } catch (error) {
        console.error("WebSocket send error:", error);
        queueMessage(messagePayload);
        setupWebSocketWithRefresh(userDetails?.streamUrl);
      }
    } else {
      queueMessage(messagePayload);
      setupWebSocketWithRefresh(userDetails?.streamUrl);
    }
  };

  const copyToClipboard = (messageEl, messageId) => {
    const clonedElement = messageEl.cloneNode(true);

    // Remove action icons from clone
    clonedElement.querySelectorAll('.msg-action-btn').forEach(el => el.remove());

    const contentToCopy = clonedElement.innerHTML;

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([contentToCopy], { type: 'text/html' }),
        'text/plain': new Blob([clonedElement.innerText || clonedElement.textContent], { type: 'text/plain' })
      })
    ]).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }).catch((err) => {
      console.error('Failed to copy:', err);
    });
  };

  const fetchUpdatedToken = async (activity) => {
    if (isAuthFailureInProgressRef.current) return;

    const request_headers = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh: userDetails.refresh })
    };
    try {
      const refreshResponse = await fetch(`${BASEURL}/api/auth/token/refresh/`, request_headers);
      if (!refreshResponse.ok) {
        console.error("fetchUpdatedToken: refresh failed with status", refreshResponse.status);
        handleAuthFailure();
        return;
      }
      const accessToken = await refreshResponse.json();
      if (!accessToken.access) {
        console.error("fetchUpdatedToken: no access token in response");
        handleAuthFailure();
        return;
      }
      const user_details = getUserDetails();
      user_details.token = accessToken.access;
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(user_details));
      if (typeof activity === 'object') {
        setTypingActive(true);
        setTimeout(() => setTypingActive(false), typing_timer);
        retrySendMessage(activity, accessToken.access);
      } else if (typeof activity === 'string') {
        reFetchPastActivities(activity, accessToken.access);
      }
    } catch (error) {
      console.error("fetchUpdatedToken error:", error);
      handleAuthFailure();
    }
  };

  const retrySendMessage = async (activity, updated_token) => {
    setupWebSocketWithRefresh(userDetails?.streamUrl);
    const request_headers = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${updated_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(activity)
    };
    try {
      const getMessageResponse = await fetch(`${BASEURL}/api/conversations/${conversationId}/activities`, request_headers);
      await getMessageResponse.json();
      setTypingActive(false);
    } catch (error) {
      setErrorMessage(ERRORMESSAGES.ERRORMESSAGESENDING);
      setTypingActive(false);
      setTimeout(() => setErrorMessage(""), error_message_timeOut);
    }
  };

  const scrollToBottom = () => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.style.scrollBehavior = "auto";
      if (chatContainer?.scrollHeight) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
      chatContainer.style.scrollBehavior = "smooth";
    }
  };

  const checkMessage = (emailId) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(emailId);
  };

  const checkMessageForSelectedRow = (message) => {
    let attachment = JSON.parse(JSON.stringify(message));
    if (attachment.body && attachment.body.length > 1) {
      if (attachment.body[1].items && attachment.body[1].items.length > 0) {
        for (const [index, plan] of attachment.body[1].items.entries()) {
          if (plan.url !== "" && plan.url === userSelectedStreamRef.current) {
            attachment.body[1].items[index].id = 'ToolSelectionStep_selected';
            break;
          }
        }
      }
    }
    return attachment;
  };

  const getMessageResponse = (message) => {
    if (message.text) {
      // Replace circled/keycap number characters with plain bullet points
      const cleanedText = message.text
        .replace(/[\u2460-\u2473\u24EA]/g, '•')           // ①②③…⑳ ⓪
        .replace(/[\u2776-\u277F]/g, '•')                  // ❶❷❸…❿ (dingbat negative circled)
        .replace(/[\u2780-\u2789]/g, '•')                  // ➀➁➂…➉ (dingbat circled sans-serif)
        .replace(/[\u278A-\u2793]/g, '•')                  // ➊➋➌…➓ (dingbat negative circled sans-serif)
        .replace(/[0-9]\uFE0F?\u20E3/g, '•');             // 1️⃣2️⃣3️⃣… (keycap emojis)
      // Convert <ol> to <ul> so numbered lists render as plain bullets
      const html = sanitizeHtml(marked.parse(cleanedText))
        .replace(/<ol\b[^>]*>/gi, '<ul>')
        .replace(/<\/ol>/gi, '</ul>');
      return (<div className="markdown-content" style={{ color: 'var(--text-primary)' }} dangerouslySetInnerHTML={{ __html: html }} />);
    } else if (message.attachments && message.attachments.length <= 1) {
      return (
        <AdaptiveCard
          className="teams_adaptive_card"
          payload={checkMessageForSelectedRow(message.attachments[0].content)}
          onExecuteAction={(event) => getCard(event, message?.activity_id)}
          hostConfig={{
            fontFamily: "Segoe UI, Helvetica Neue, sans-serif",
          }}
        />
      );
    } else if (message.attachments && message.attachments.length > 1) {
      return (
        <Carousel showArrows={true} swipeable={false}>
          {message.attachments.map((attachment) =>
            <AdaptiveCard
              key={attachment.id}
              payload={attachment.content}
              onExecuteAction={(event) => getCard(event, message?.activity_id)}
              hostConfig={{
                fontFamily: "Segoe UI, Helvetica Neue, sans-serif",
              }}
            />
          )}
        </Carousel>
      );
    } else if (message.type === "beagle_cards") {
      let data = message.config;
      let chart_config = data?.template_config?.chart_config?.find(config => config.chart_id === data?.template_config?.default_template_view?.chart_id);
      return (
        <div id="card-builder-container" className="card-builder-container">
          {data.template_config ? (
            <BeagleCardLibrary
              display_card_name={true}
              template={data.template_config.id}
              template_config={data.template_config}
              render_platform={'teams'}
              chart_view={!(data?.template_config?.default_template_view.chart_id === 1 ||
                Object.keys(data?.template_config?.default_template_view || {}).length === 0)}
              chart_config={{
                chart_id: chart_config?.chart_id,
                chart_options: chart_config?.chart_options,
                chart_name: chart_config?.chart_name
              }}
              unique_table_data={data.unique_table_data}
              table_data={data.table_data}
              teams_filter={data.teams_filter}
            />
          ) : null}
        </div>
      );
    } else {
      return null;
    }
  };

  const getCard = async (event, activityId) => {
    if (userDetails) {
      if (event?._processedData) {
        setupWebSocketWithRefresh(userDetails?.streamUrl);
        setTypingActive(true);
        setTimeout(() => setTypingActive(false), typing_timer);

        if (event._processedData.hasOwnProperty("action") && event._processedData.action === "open_stream") {
          userSelectedStreamRef.current = event._processedData.stream_url || "user-manual";
          setUserSelectedStream(true);
          if (streamUrl === event._processedData.stream_url && startResearch) return;

          setStartResearch(true);
          setStreamHeading(event._processedData.heading || "Research Plan");
          setStreamStatus(event._processedData.status || "completed");
          setStreamUrl(event._processedData.stream_url);
          return;
        }

        scrollToBottom();
        const activity = {
          type: "action",
          tool: activeLeftBtn,
          value: event._processedData,
          id: activityId,
          from: { id: userDetails.email },
          tenant_id: userDetails.tenantId,
          localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const request_headers = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${userDetails.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(activity)
        };

        if (activity?.value?.msteams?.type === 'task/fetch') {
          setTeamsUrl(activity?.value?.redirect_url);
          dispatch(setOpenModal(true));
        }

        try {
          const getMessageResponse = await fetch(`${BASEURL}/api/conversations/${conversationId}/activities`, request_headers);
          const responseData = await getMessageResponse.json();
          if (responseData.code === "token_not_valid") {
            fetchUpdatedToken(activity);
          }
          setInputMessage("");
          setTypingActive(false);
        } catch (error) {
          setErrorMessage(ERRORMESSAGES.ERRORMESSAGESENDING);
          setTimeout(() => setErrorMessage(""), error_message_timeOut);
        }
        setTypingActive(false);
      }
    }
  };

  const clickListener = (e) => {
    if (e.target.nodeName === 'IMG' && e.target.className === "ac-image") {
      setImagePopup(true);
      setImageSrc(e.target.currentSrc);
    }
  };

  // Auto-dismiss error message after timeout
  useEffect(() => {
    if (!errorMessage) return;
    const dismissTimer = setTimeout(() => setErrorMessage(''), error_message_timeOut);
    return () => clearTimeout(dismissTimer);
  }, [errorMessage]);

  const handleScroll = () => {
    const chatContainer = chatContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = chatContainer;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 100;
    setIsUserScrolledUp(!isAtBottom);
  };

  useEffect(() => {
    if (!queriesModal) return;
    const handleClickOutside = (event) => {
      const inputGroup = document.querySelector('.chat-input-group');
      const queriesModalDiv = document.querySelector('.va-queries-modal');
      if (
        queriesModal &&
        !inputGroup?.contains(event.target) &&
        !queriesModalDiv?.contains(event.target)
      ) {
        setQueriesModal(false);
        setActiveVAIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [queriesModal]);

  const selectQuery = (e, prompt) => {
    setInputMessage(prompt);
    setTypingActive(true);
    sendMessage(prompt);
    setQueriesModal(false);
    setActiveVAIndex(-1);
    setIsUserScrolledUp(false);
    scrollToBottom();
  };

  useEffect(() => {
    return () => {
      if (recognizerRef.current) {
        try { recognizerRef.current.close(); } catch (e) { /* ignore cleanup errors */ }
        recognizerRef.current = null;
      }
    };
  }, []);

  const handleSpeechToText = () => {
    // Cancel if currently listening
    if (micState && recognizerRef.current) {
      recognizerRef.current.close();
      recognizerRef.current = null;
      setMicState(false);
      return;
    }

    if (!token || !region) {
      setErrorMessage('Voice input is unavailable. Speech credentials are missing.');
      return;
    }

    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(token, region);
      speechConfig.speechRecognitionLanguage = 'en-US';

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      recognizerRef.current = recognizer;

      // Show interim results while speaking
      recognizer.recognizing = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          setInputMessage(e.result.text);
        }
      };

      setMicState(true);

      // recognizeOnceAsync auto-stops after detecting silence
      recognizer.recognizeOnceAsync(
        (result) => {
          if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
            setInputMessage(result.text);
          } else if (result.reason === SpeechSDK.ResultReason.NoMatch) {
            // No speech detected — silently dismiss
          } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
            const cancellation = SpeechSDK.CancellationDetails.fromResult(result);
            if (cancellation.reason === SpeechSDK.CancellationReason.Error) {
              console.error('Speech recognition error:', cancellation.errorCode, cancellation.errorDetails);
              setErrorMessage('Speech recognition failed. Please try again.');
            }
          }
          recognizer.close();
          recognizerRef.current = null;
          setMicState(false);
        },
        (err) => {
          console.error('Speech recognition error:', err);
          setErrorMessage('Speech recognition failed. Please try again.');
          recognizer.close();
          recognizerRef.current = null;
          setMicState(false);
        }
      );
    } catch (err) {
      console.error('Speech recognition setup error:', err);
      setErrorMessage('Voice input is unavailable. Please try again later.');
    }
  };

  const handleShowHistory = () => {
    setIsUserScrolledUp(false);
    activateChat();
    setTimeout(() => scrollToBottom(), 0);
    setTimeout(() => scrollToBottom(), 1000);
  };

  const capitalize = s => s && String(s[0]).toUpperCase() + String(s).slice(1);

  const shareImage = async (imageUrl, fileName = 'image.jpg') => {
    if (navigator.share && navigator.canShare) {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: blob.type });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Check this out!', text: 'Look at this image!' });
        }
      } catch (error) {
        console.error('Error sharing the image:', error);
      }
    } else {
      const message = encodeURIComponent(`Check this out! ${imageUrl}`);
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }
  };

  // Tool button data
  const toolButtons = [
    { key: 'search', label: 'Search', icon: Search },
    { key: 'research', label: 'Deep Research', icon: FlaskConical },
    { key: 'prescriptive', label: 'Frameworks', icon: LayoutGrid, disabled: !userDetails?.is_beta_tester },
  ];

  return (
    <>
      {/* Error Banner */}
      {errorMessage && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 text-sm font-medium text-white bg-red-500 shadow-lg">
          <div className="flex items-center gap-2">
            <AlertCircle size={16} />
            {errorMessage}
          </div>
          <button onClick={() => setErrorMessage('')} className="p-1 rounded hover:bg-red-600 transition-colors">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Connection Status Banner */}
      {connectionStatus !== 'connected' && (
        <div className={[
          "fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white",
          connectionStatus === 'reconnecting' ? 'bg-amber-500' : '',
          connectionStatus === 'disconnected' ? 'bg-red-500' : '',
          connectionStatus === 'offline' ? 'bg-gray-500' : '',
        ].join(' ')}>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          {connectionStatus === 'reconnecting' && 'Reconnecting to server...'}
          {connectionStatus === 'disconnected' && 'Connection lost. Redirecting to login...'}
          {connectionStatus === 'offline' && 'You are offline. Messages will be sent when back online.'}
          {pendingMessages.length > 0 && (
            <span className="ml-1 opacity-90">({pendingMessages.length} pending)</span>
          )}
        </div>
      )}

      {/* Main Chat Layout */}
      <div className="flex overflow-hidden h-full bg-[var(--bg-secondary)]">
        {/* Chat Area */}
        <div
          className={[
            "flex flex-col h-full",
            startResearch && streamUrl ? "w-[45%] max-md:hidden" : "w-full",
          ].join(' ')}
          onClick={(e) => clickListener(e)}
          onKeyDown={(e) => { if (e.key === 'Enter') clickListener(e); }}
        >
          <div className={`flex flex-col flex-1 min-h-0 ${!showChat ? 'chat-empty-state' : ''}`}>
            {/* Chat Messages */}
            {showChat && (
              <div
                className={`flex-1 overflow-y-auto scroll-smooth ${shouldAnimateRef.current ? 'chat-messages-enter' : ''}`}
                id="chats"
                ref={chatContainerRef}
                onScroll={handleScroll}
                style={{ scrollbarWidth: 'thin' }}
              >
                <div className="mx-auto w-full max-w-4xl px-4 max-sm:px-2 py-4 space-y-3 min-h-full">
                {messages?.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-3xl font-bold" style={{ color: 'var(--text-tertiary)' }}>
                      {CHATCONST.BEAGLEGPT}
                    </p>
                  </div>
                )}

                {messages?.map((message) => {
                  if (message.type !== "message" && message.type !== "beagle_cards") return null;

                  const isUserMessage = message.from?.id ? checkMessage(message.from.id) : false;
                  const hasAssistantContent = message.text || message.attachments?.length > 0 || message.type === "beagle_cards";

                  if (isUserMessage && message.type === "action") return null;

                  const messageId = message.activity_id || message.id;
                  const isTtsActive = activeTtsMessageId === messageId;

                  return (
                    <div
                      className={[
                        "flex",
                        isUserMessage ? "justify-end" : "justify-start",
                      ].join(' ')}
                      key={messageId}
                    >
                      {isUserMessage ? (
                        /* User Message Bubble */
                        <div
                          className="max-w-[85%] max-sm:max-w-[92%] px-3 py-2 sm:px-4 sm:py-3 rounded-2xl rounded-br-md text-xs sm:text-sm select-text overflow-hidden"
                          style={{
                            background: 'var(--brand-primary)',
                            color: '#ffffff',
                            opacity: message.pending ? 0.7 : 1,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          <div style={{ whiteSpace: 'pre-line', margin: 0, overflowWrap: 'anywhere' }}>{message.text}</div>
                        </div>
                      ) : hasAssistantContent ? (
                        /* Assistant Message Bubble */
                        <div className="group flex items-start gap-1 max-w-[90%] max-sm:max-w-[92%] overflow-x-hidden">
                          <div
                            className="min-w-0 px-3 py-2 sm:px-4 sm:py-3 rounded-2xl rounded-bl-md text-xs sm:text-sm select-text"
                            style={{
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border-color)',
                              overflowWrap: 'anywhere',
                            }}
                            data-message-id={messageId}
                          >
                            {getMessageResponse(message)}
                          </div>
                          {/* Action buttons — right side, vertical stack, always visible */}
                          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                            <button
                              className="msg-action-btn p-1 rounded-md transition-colors"
                              style={{ color: 'var(--text-tertiary)' }}
                              onClick={(e) => {
                                const msgEl = e.currentTarget.closest('.group').querySelector('[data-message-id]');
                                copyToClipboard(msgEl, messageId);
                              }}
                              title="Copy"
                            >
                              {copiedMessageId === messageId ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                            </button>
                            <button
                              className="msg-action-btn p-1 rounded-md transition-all"
                              style={{
                                color: isTtsActive && ttsState === 'playing' ? '#EF4444'
                                     : isTtsActive && ttsState === 'loading' ? 'var(--text-primary)'
                                     : 'var(--text-tertiary)',
                                backgroundColor: isTtsActive && ttsState === 'playing' ? 'rgba(239, 68, 68, 0.1)'
                                               : isTtsActive && ttsState === 'loading' ? 'rgba(0, 0, 0, 0.05)'
                                               : 'transparent',
                              }}
                              onClick={(e) => {
                                if (isTtsActive && ttsState !== 'idle') {
                                  stopSpeech();
                                  return;
                                }
                                const msgEl = e.currentTarget.closest('.group').querySelector('[data-message-id]');
                                const content = msgEl?.innerText || msgEl?.textContent || '';
                                if (content.trim()) {
                                  setActiveTtsMessageId(messageId);
                                  textToSpeech(content, e.currentTarget);
                                }
                              }}
                              title={isTtsActive && ttsState === 'playing' ? 'Stop narration'
                                   : isTtsActive && ttsState === 'loading' ? 'Starting narration...'
                                   : 'Narrate'}
                            >
                              {isTtsActive && ttsState === 'playing' ? <Square size={14} fill="currentColor" />
                               : isTtsActive && ttsState === 'loading' ? <Loader2 size={14} className="animate-spin" />
                               : <Volume2 size={14} />}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {/* Typing Indicator */}
                {typingActive && (
                  <div className="flex items-center gap-2 px-2 py-1">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Processing</span>
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '0ms', background: 'var(--brand-primary)' }} />
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '150ms', background: 'var(--brand-primary)' }} />
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '300ms', background: 'var(--brand-primary)' }} />
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}

            {/* Welcome / Empty State */}
            {!showChat && (
              <div className="flex items-center justify-center max-sm:px-4 mb-8 max-sm:mb-4">
                <p className="text-2xl font-medium max-sm:text-base text-center" style={{ color: 'var(--text-tertiary)' }}>
                  {isMobile
                    ? `Hi ${capitalize(userDetails?.first_name) || 'there'}, how can I help?`
                    : `Hi ${capitalize(userDetails?.first_name) || 'there'}, What insight are you looking for today?`
                  }
                </p>
              </div>
            )}

            {/* Jump to bottom */}
            {isUserScrolledUp && showChat && (
              isMobile ? (
                <div className="mx-auto w-full max-w-4xl px-4 max-sm:px-2 flex justify-end" style={{ marginBottom: '-16px', position: 'relative', zIndex: 5 }}>
                  <button
                    onClick={() => scrollToBottom()}
                    className="flex items-center justify-center w-8 h-8 rounded-full shadow-md transition-all hover:shadow-lg"
                    style={{
                      background: 'var(--card-bg)',
                      color: 'var(--text-secondary)',
                      border: '1px solid var(--border-color)',
                    }}
                    aria-label="Jump to bottom"
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex justify-center pb-2">
                  <button
                    onClick={() => scrollToBottom()}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium shadow-md transition-all hover:shadow-lg"
                    style={{
                      background: 'var(--card-bg)',
                      color: 'var(--text-secondary)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    <ChevronDown size={14} />
                    Jump to bottom
                  </button>
                </div>
              )
            )}

            {/* Chat Input Area */}
            <div ref={inputWrapperRef} className="mx-auto w-full max-w-4xl px-4 max-sm:px-2 pb-2">
              <div
                className={[
                  "chat-input-group rounded-2xl border-2 transition-colors",
                  queriesModal ? "rounded-b-none" : "",
                ].join(' ')}
                style={{
                  background: 'var(--card-bg)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <textarea
                  placeholder={micState ? "Listening..." : CHATCONST.QUERYPLACEHOLDER}
                  ref={textareaRef}
                  onChange={handleInputChange}
                  disabled={isDprQueryRunningRef.current}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey && inputMessage.length > 0) {
                      event.preventDefault();
                      sendMessage();
                      const textarea = textareaRef.current;
                      textarea.style.height = "auto";
                    }
                  }}
                  value={inputMessage}
                  className="w-full resize-none border-0 bg-transparent outline-none p-3 text-sm hide-scrollbar"
                  style={{ color: 'var(--text-primary)' }}
                  rows={1}
                />

                {/* Toolbar Row */}
                <div className="flex items-center justify-between px-3 pb-2">
                  {/* Left: Tool buttons */}
                  <div className="flex items-center gap-1.5">
                    {toolButtons.map((btn) => {
                      const isActive = activeLeftBtn === btn.key;
                      const IconComp = btn.icon;
                      return (
                        <button
                          key={btn.key}
                          onClick={() => setActiveLeftBtn(btn.key)}
                          disabled={btn.disabled}
                          title={btn.label}
                          className={[
                            "flex items-center gap-1.5 px-2.5 py-1.5 max-sm:p-2 rounded-lg text-xs font-medium transition-all",
                            "border disabled:opacity-40 disabled:cursor-not-allowed",
                            isActive
                              ? "text-white border-transparent"
                              : "border-transparent hover:border-[var(--border-color)]",
                          ].join(' ')}
                          style={
                            isActive
                              ? { background: 'var(--brand-primary)', color: '#ffffff' }
                              : { color: 'var(--text-secondary)', background: 'transparent' }
                          }
                        >
                          <IconComp size={14} />
                          {!isMobile && <span>{btn.label}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Right: Mic + Send */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleSpeechToText}
                      disabled={isDprQueryRunningRef.current}
                      className={[
                        "p-2 rounded-lg transition-all",
                        micState ? "text-white" : "",
                      ].join(' ')}
                      style={
                        micState
                          ? { background: 'var(--brand-primary)', color: '#ffffff' }
                          : { color: 'var(--text-tertiary)' }
                      }
                      title="Voice input"
                    >
                      <Mic size={18} />
                    </button>
                    <button
                      onClick={() => sendMessage()}
                      disabled={inputMessage.length === 0 || isDprQueryRunningRef.current}
                      className="p-2 rounded-lg text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: inputMessage.length > 0 ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
                      title="Send message"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* VA Queries Modal */}
              {queriesModal && activeVAIndex >= 0 && !showChat && (
                <div
                  className="va-queries-modal rounded-b-2xl border-2 border-t-0 overflow-hidden max-sm:max-h-60 max-sm:overflow-y-auto"
                  style={{
                    borderColor: 'var(--border-color)',
                    background: 'var(--card-bg)',
                  }}
                >
                  {vaPrompts[activeVAIndex].prompts.length > 0 && vaPrompts[activeVAIndex].prompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 px-4 py-3 text-sm cursor-pointer transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={(e) => selectQuery(e, prompt)}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Search size={14} className="shrink-0 opacity-50" />
                      {prompt}
                    </div>
                  ))}
                </div>
              )}

              {/* Show History + VA Buttons */}
              {!showChat && !queriesModal && (
                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleShowHistory}
                    className="text-sm font-medium transition-colors hover:underline"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    Show History
                  </button>
                </div>
              )}
              {!showChat && !queriesModal && !isMobile && vaPrompts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 max-h-60 overflow-auto">
                  {vaPrompts.map((va, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setActiveVAIndex(index);
                        setQueriesModal(true);
                      }}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:shadow-sm"
                      style={{
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-secondary)',
                        background: 'var(--bg-secondary)',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'var(--brand-primary)';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.borderColor = 'var(--brand-primary)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      {va.va_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <ModalComponent teamsUrl={teamsUrl} />
        </div>

        {/* Research / Stream Panel */}
        {startResearch && streamUrl && (
          <div
            className={[
              "border-l overflow-x-hidden overflow-y-auto",
              isMobile ? "fixed inset-x-0 bottom-0 top-[52px] z-30" : "w-[55%]", /* top-[52px] = header height */
            ].join(' ')}
            style={{
              borderColor: 'var(--border-color)',
              background: 'var(--bg-primary)',
            }}
          >
            <StreamViewer
              setStartResearch={setStartResearch}
              setStreamUrl={setStreamUrl}
              streamUrl={streamUrl}
              status={streamStatus}
              heading={streamHeading}
              setUserSelectedStream={clearUserSelectedStream}
            />
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {imagePopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          tabIndex={-1}
          ref={(el) => el?.focus()}
          onKeyDown={(e) => { if (e.key === "Escape") setImagePopup(false); }}
          onClick={(e) => { if (e.target === e.currentTarget) setImagePopup(false); }}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <div className="flex gap-2 mb-3 justify-end">
              <button
                onClick={() => shareImage(imageSrc)}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Share image"
              >
                <Share2 size={18} />
              </button>
              <button
                onClick={() => setImagePopup(false)}
                data-testid="close_btn"
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </div>
            <img
              ref={imageRef}
              src={imageSrc}
              alt="Expanded view"
              className="max-h-[80vh] max-w-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatComponent;
